// src/lib/osm-lgs.ts
// Robust OSM (Overpass) fallback for MTG-friendly stores (LGS).
// - Rotates across multiple Overpass endpoints
// - Targets shops likely to host/carry Magic
// - Scores + dedupes results
// - Returns sorted list by score then distance

export type OSMStore = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address?: string;
  distanceKm?: number;
  tags?: Record<string, string>;
  source: "osm";
};

const OVERPASS_ENDPOINTS = [
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
  "https://overpass.openstreetmap.fr/api/interpreter",
];

// Avoid obvious big-box / irrelevant chains
const BLOCKLIST_RE = /(game\s*stop|walmart|target|barnes\s*&?\s*noble|best\s*buy)/i;

// Helpful name cues
const NAME_SIGNAL_RE = /\b(mtg|magic|trading\s*card|tcg|game\s*store|tabletop|board\s*game|hobby|comics?)\b/i;

// Some shops tag their offerings explicitly
const TAG_SIGNALS = [
  "trading_cards",
  "collectible_trading_cards",
  "tabletop_games",
  "board_games",
  "ccg",
] as const;

type OverpassElement = {
  id: number | string;
  type: "node" | "way" | "relation";
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
};

function kmDistance(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la = toRad(a.lat);
  const lb = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la) * Math.cos(lb) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function addressFromTags(t: Record<string, string> = {}) {
  const parts = [
    t["addr:housenumber"],
    t["addr:street"],
    t["addr:city"],
    t["addr:state"] ?? t["addr:province"],
    t["addr:postcode"],
  ].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return t["addr:full"] || "";
}

function scoreShop(name: string, tags: Record<string, string> = {}) {
  if (BLOCKLIST_RE.test(name) || BLOCKLIST_RE.test(tags.brand ?? "")) return -1e6;

  let s = 0;
  const shop = (tags.shop ?? "").toLowerCase();

  // Base by shop type
  if (shop === "games") s += 6;
  else if (shop === "hobby") s += 4;
  else if (shop === "toys") s += 2;
  else if (shop === "books") s += 1;
  else if (shop === "video_games") s -= 2; // likely not card/tabletop

  // Signals in tags
  for (const k of TAG_SIGNALS) {
    if ((tags[k] ?? "").toLowerCase() === "yes") s += 5;
  }

  // Name cues
  if (NAME_SIGNAL_RE.test(name)) s += 4;
  if (/magic|mtg/i.test(name)) s += 4;
  if (/trading\s*card|tcg/i.test(name)) s += 3;
  if (/tabletop|board\s*game/i.test(name)) s += 2;
  if (/hobby|comics?/i.test(name)) s += 1;

  return s;
}

/** Build a balanced Overpass QL targeting good candidates.
 * We *do not* over-constrain on the server side to keep recall high;
 * client-side scoring/ranking handles precision.
 */
function buildOverpassQuery(lat: number, lon: number, radiusMeters: number) {
  // We search across a few shop categories + explicit hobby/card signals
  return `
    [out:json][timeout:60];
    (
      nwr(around:${radiusMeters},${lat},${lon})[shop~"^games|hobby|toys|books|collector|video_games$"];
      nwr(around:${radiusMeters},${lat},${lon})[trading_cards=yes];
      nwr(around:${radiusMeters},${lat},${lon})[collectible_trading_cards=yes];
      nwr(around:${radiusMeters},${lat},${lon})[board_games=yes];
      nwr(around:${radiusMeters},${lat},${lon})[tabletop_games=yes];
      nwr(around:${radiusMeters},${lat},${lon})[ccg=yes];
      // Some places don't tag 'shop', but name hints help
      nwr(around:${radiusMeters},${lat},${lon})[name~"Magic|MTG|Trading\\s*Card|Game\\s*Store|Tabletop|Board\\s*Game|TCG|Comics?|Hobby",i];
    );
    out center tags;
  `;
}

async function callOverpass(endpoint: string, query: string, signal?: AbortSignal) {
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      // User-Agent is ignored by browsers but kept for completeness if moved server-side.
    },
    body: `data=${encodeURIComponent(query)}`,
    signal,
  });
  if (!res.ok) throw new Error(`Overpass ${res.status}`);
  return (await res.json()) as { elements?: OverpassElement[] };
}

function normalizeEl(e: OverpassElement): OSMStore | null {
  const lat = typeof e.lat === "number" ? e.lat : e.center?.lat;
  const lon = typeof e.lon === "number" ? e.lon : e.center?.lon;
  if (typeof lat !== "number" || typeof lon !== "number") return null;

  const tags = e.tags ?? {};
  const name = (tags.name || tags["addr:housename"] || "Game Store").toString();
  return {
    id: `${e.type}/${e.id}`,
    name,
    lat,
    lng: lon,
    address: addressFromTags(tags),
    tags,
    source: "osm",
  };
}

function dedupeAndRank(
  stores: OSMStore[],
  center: { lat: number; lon: number },
  maxResults = 150,
) {
  // Compute distance & score
  const enriched = stores.map((s) => {
    const distanceKm = kmDistance(center, { lat: s.lat, lon: s.lng });
    const score = scoreShop(s.name, s.tags);
    return { ...s, distanceKm, _score: score };
  });

  // Drop obviously blocked or missing name
  let filtered = enriched.filter((s) => s._score > -1e5 && s.name.trim().length > 0);

  // Deduplicate by (normalized name) within ~50m
  const out: OSMStore[] = [];
  const seen = new Set<string>();
  for (const s of filtered.sort((a, b) => a.distanceKm! - b.distanceKm!)) {
    const key = `${s.name.toLowerCase().replace(/\s+/g, " ").trim()}`;
    // If another with same name within 0.05 km already kept, skip
    let skip = false;
    for (const id of Array.from(seen)) {
      if (id === key) {
        const existing = out.find((x) => x.name.toLowerCase().replace(/\s+/g, " ").trim() === key);
        if (existing) {
          const d = kmDistance({ lat: existing.lat, lon: existing.lng }, { lat: s.lat, lon: s.lng });
          if (d <= 0.05) { // ~50 meters
            skip = true;
            break;
          }
        }
      }
    }
    if (!skip) {
      seen.add(key);
      out.push(s);
    }
  }

  // Final rank: score desc, then distance asc, then name
  const ranked = out
    .sort((a: any, b: any) => {
      if (b._score !== a._score) return b._score - a._score;
      if ((a.distanceKm ?? 0) !== (b.distanceKm ?? 0)) return (a.distanceKm ?? 0) - (b.distanceKm ?? 0);
      return a.name.localeCompare(b.name);
    })
    .slice(0, maxResults)
    .map(({ _score, ...s }) => s); // strip internal field

  return ranked;
}

/** Strong OSM fallback for nearby WPN-like stores */
export async function searchLgsOSM(
  lat: number,
  lng: number,
  radiusMiles = 100,
  signal?: AbortSignal,
): Promise<OSMStore[]> {
  const radiusMeters = Math.min(Math.round(radiusMiles * 1609.34), 200_000); // clamp ~200km
  const q = buildOverpassQuery(lat, lng, radiusMeters);

  let lastErr: unknown = null;
  for (const ep of OVERPASS_ENDPOINTS) {
    try {
      const json = await callOverpass(ep, q, signal);
      const normalized = (json.elements ?? [])
        .map(normalizeEl)
        .filter(Boolean) as OSMStore[];

      if (!normalized.length) continue;
      return dedupeAndRank(normalized, { lat, lon: lng });
    } catch (e) {
      lastErr = e;
      // try next
    }
  }

  // Nothing worked
  // eslint-disable-next-line no-console
  console.warn("OSM fallback failed", lastErr);
  return [];
}

// Fetch nearby game / comic / hobby stores via Overpass (basic heuristic).
// radiusKm: numeric radius in km.
export async function fetchNearbyLgs(lat: number, lng: number, radiusKm = 15): Promise<Array<{
  id: string;
  lat: number;
  lng: number;
  name: string;
  address?: string;
}>> {
  const radiusM = Math.round(radiusKm * 1000);
  const query = `
[out:json][timeout:25];
(
  node(around:${radiusM},${lat},${lng})[shop=games];
  node(around:${radiusM},${lat},${lng})[shop=collectibles];
  node(around:${radiusM},${lat},${lng})[shop=hobby];
);
out tags center 40;
`;
  const res = await fetch("https://overpass-api.de/api/interpreter", {
    method: "POST",
    body: query,
    headers: { "Content-Type": "text/plain" },
  });
  if (!res.ok) throw new Error("OSM request failed " + res.status);
  const data = await res.json();
  if (!data?.elements) return [];
  return data.elements
    .filter((e: any) => typeof e.lat === "number" && typeof e.lon === "number")
    .map((e: any) => ({
      id: String(e.id),
      lat: e.lat,
      lng: e.lon,
      name: e.tags?.name || "Game Store",
      address: [e.tags?.housenumber, e.tags?.street, e.tags?.city]
        .filter(Boolean)
        .join(" "),
    }));
}

export default fetchNearbyLgs;

